// Sawyer 12 incident-wording-pet-notes (F3) — Settings → Operations →
// "Words your team sees". The business owner names the safety and liability
// log (default "Incident"); the word changes every admin surface at once,
// without a deploy (US2). HTHD has not chosen the word yet (open HTHD
// question 15), so nothing changes for anyone until an owner saves one.
//
// States (DESIGN-PHILOSOPHY's five): loading "Loading…"; error "Could not load
// these words. Try again." + Retry; default (the word marked "Default");
// saving; saved with who/when; save error keeping the typed words; read-only
// for every role without business-config:write:all. On a server that predates
// the route (403 route_not_mapped, the deploy window) the card renders NOTHING
// rather than a Retry that can never succeed (refuter C7).
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Input } from '@ferro/ui';
import { incidentNouns, validateVocabWord, VOCAB_WORD_MESSAGES } from '../../lib/tenant-vocabulary.js';
import { adminTenantVocabularyApi } from '../../lib/tenant-vocabulary-api.js';
import { setTenantVocabulary, useTenantVocabulary } from '../../lib/useTenantVocabulary.js';

const TITLE = 'Words your team sees';
const LEAD =
  "Choose what the safety and liability log is called. Pet notes on a dog's profile stay separate whatever you choose.";

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

interface WordingSettingsCardProps {
  apiUrl: string;
  canEdit: boolean;
}

export function WordingSettingsCard({ apiUrl, canEdit }: WordingSettingsCardProps): JSX.Element | null {
  const { status, view, incident, reload } = useTenantVocabulary(apiUrl);
  const configured = view?.source.incidentLabel === 'configured';

  const [singular, setSingular] = useState('');
  const [plural, setPlural] = useState('');
  const [pluralEdited, setPluralEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedPlural, setSavedPlural] = useState<string | null>(null);

  // Hydrate the inputs from the stored word (empty when on the default).
  const storedSingular = configured ? (view?.incidentLabel.singular ?? '') : '';
  const storedPlural = configured ? (view?.incidentLabel.plural ?? '') : '';
  useEffect(() => {
    setSingular(storedSingular);
    setPlural(storedPlural);
    setPluralEdited(storedPlural.length > 0);
  }, [storedSingular, storedPlural]);

  const typed = singular.trim().length > 0 || plural.trim().length > 0;
  const singularError = typed ? validateVocabWord(singular) : null;
  const pluralError = typed ? validateVocabWord(plural) : null;
  const valid = typed && singularError === null && pluralError === null;
  const changed = !configured || singular.trim() !== storedSingular || plural.trim() !== storedPlural;
  const preview = useMemo(
    () => (valid ? incidentNouns({ singular: singular.trim(), plural: plural.trim() }, 'configured') : null),
    [valid, singular, plural],
  );

  if (status === 'not_deployed') return null;

  if (status === 'loading') {
    return (
      <Card title={TITLE}>
        <p className="ferro-muted" role="status" data-testid="wording-settings-loading">
          Loading…
        </p>
      </Card>
    );
  }

  if (status === 'unavailable' || !view) {
    return (
      <Card title={TITLE}>
        <div data-testid="wording-settings-error">
          <Alert variant="error">Could not load these words. Try again.</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={reload} data-testid="wording-retry">
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const save = async (incidentLabel: { singular: string; plural: string } | null): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    setSavedPlural(null);
    const res = await adminTenantVocabularyApi.update(apiUrl, { incidentLabel });
    setSaving(false);
    if (res.error || !res.data) {
      setSaveError(res.error ?? 'Could not save those words. Please try again.');
      return;
    }
    const { changed: _changed, audited: _audited, ...next } = res.data;
    setTenantVocabulary(apiUrl, next);
    setSavedPlural(next.incidentLabel.plural);
  };

  const defaultMark = configured ? null : (
    <span data-testid="wording-default-mark">
      <Badge variant="platform">Default</Badge>
    </span>
  );

  return (
    <Card title={TITLE} subtitle={LEAD}>
      <div className="ferro-settings__card" data-testid="wording-settings-ready">
        {view.lastChange ? (
          <p className="ferro-settings__last-change" data-testid="wording-last-change">
            Last changed by <strong>{view.lastChange.byName}</strong>
            {view.lastChange.at ? `, ${whenLabel(view.lastChange.at)}` : ''}
          </p>
        ) : null}

        <p data-testid="wording-current">
          Your team sees: <strong>{incident.many}</strong> {defaultMark}
        </p>

        {canEdit ? (
          <form
            className="ferro-form ferro-settings__form"
            noValidate
            aria-label={TITLE}
            onSubmit={(e) => {
              e.preventDefault();
              if (valid && changed && !saving) void save({ singular: singular.trim(), plural: plural.trim() });
            }}
          >
            <div className="ferro-settings__field">
              <Input
                name="incidentSingular"
                label="One"
                value={singular}
                placeholder="Incident"
                maxLength={60}
                disabled={saving}
                error={singularError ?? undefined}
                onChange={(e) => {
                  const next = e.target.value;
                  setSingular(next);
                  setSavedPlural(null);
                  if (!pluralEdited) setPlural(next.trim().length > 0 ? `${next.trimEnd()}s` : '');
                }}
                data-testid="wording-singular"
              />
            </div>
            <div className="ferro-settings__field">
              <Input
                name="incidentPlural"
                label="More than one"
                value={plural}
                placeholder="Incidents"
                maxLength={60}
                disabled={saving}
                error={pluralError ?? undefined}
                onChange={(e) => {
                  setPlural(e.target.value);
                  setPluralEdited(true);
                  setSavedPlural(null);
                }}
                data-testid="wording-plural"
              />
            </div>

            {preview ? (
              <p className="ferro-muted" data-testid="wording-preview">
                Your team will see: {preview.many} · {preview.newAction} · {preview.aOne} is filed
              </p>
            ) : !typed ? (
              <p className="ferro-muted ferro-settings__readonly-hint">{VOCAB_WORD_MESSAGES.required}</p>
            ) : null}

            {saveError ? (
              <div data-testid="wording-save-error">
                <Alert variant="error">{saveError}</Alert>
              </div>
            ) : null}
            {savedPlural && !saveError ? (
              <div data-testid="wording-save-success">
                <Alert variant="success">{`Saved. Your team now sees "${savedPlural}".`}</Alert>
              </div>
            ) : null}

            <div className="ferro-settings__actions">
              <Button
                type="submit"
                variant="primary"
                isLoading={saving}
                disabled={!valid || !changed || saving}
                data-testid="wording-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {configured ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={saving}
                  onClick={() => void save(null)}
                  data-testid="wording-use-default"
                >
                  Use the default (Incident)
                </Button>
              ) : null}
            </div>
          </form>
        ) : (
          <p className="ferro-muted ferro-settings__readonly-hint" data-testid="wording-readonly">
            Only a business owner or manager can change these words.
          </p>
        )}
      </div>
    </Card>
  );
}
